import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ApplicationType, AuthErrorState, AuthErrorStateType, ROUTES } from '@maia/config';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { catchError, EMPTY, Observable, throwError } from 'rxjs';
import { AppContextService } from '../services/app-context.service';

const UNAUTHORIZED_STATUS: Readonly<number> = 401;
const FORBIDDEN_STATUS: Readonly<number> = 403;
const AUTH_ERROR_STATUSES: Readonly<number[]> = [UNAUTHORIZED_STATUS, FORBIDDEN_STATUS];
const AUTHORIZATION_HEADER: Readonly<string> = 'Authorization';

@Injectable()
export class AuthErrorInterceptor implements HttpInterceptor {
  private securityService: OidcSecurityService = inject(OidcSecurityService);
  private appContext: AppContextService = inject(AppContextService);
  private router: Router = inject(Router);

  public intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!req.headers.has(AUTHORIZATION_HEADER)) return next.handle(req);

    return next.handle(req).pipe(
      catchError((error: unknown) => {
        if (!this.isAuthError(error)) return throwError(() => error);

        this.handleAuthError((error as HttpErrorResponse).status);
        return EMPTY;
      }),
    );
  }

  private isAuthError(error: unknown): boolean {
    return error instanceof HttpErrorResponse && AUTH_ERROR_STATUSES.includes(error.status);
  }

  private handleAuthError(status: number): void {
    const activeApplication: ApplicationType | null = this.appContext.selectedApplication();
    this.securityService.logoffLocal(activeApplication ?? undefined);
    const errorState: AuthErrorStateType =
      status === UNAUTHORIZED_STATUS ? AuthErrorState.SessionExpired : AuthErrorState.Forbidden;
    this.router.navigate([ROUTES.picker], { state: { error: errorState } });
  }
}
